import { Button, Grid } from '@mui/material';
import { createContext, useEffect, useState } from 'react';
import { SectionServices } from './SectionServices';
import Swal from 'sweetalert2';
import SectionList from './SectionList';
import Loading from '../../../../components/Loader/Loading';
import PageHeaderLayout from '../../../../components/Layout/PageHeaderLayout';
import { Add, ListAlt, PhoneAndroid } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import MobilePreview from './MobilePreview';

export const SectionContext = createContext();

const Sections = ({ embedded = false }) => {
    const navigate = useNavigate();
    const menuDispatch = useDispatch();
    useEffect(() => {
        menuDispatch({
            type: 'menuSelect',
            payload: embedded ? 'home-screen' : 'sections'
        });
    }, [embedded, menuDispatch]);
    const [allRowCount, setAllRowCount] = useState(0);
    const [list, setList] = useState([]);
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
        pageType: 'section',
        filters: {
            searchKey: "",
            sortBy: 'position',
            sortType: 'ASC',
            filterOptions: []
        }
    });
    const [loading, setLoading] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [previewOpen, setPreviewOpen] = useState(false);

    useEffect(() => {
        const debounceTimer = setTimeout(() => {
            setPaginationModel(prev => {
                if (prev.filters.searchKey === searchInput) {
                    return prev;
                }

                return {
                    ...prev,
                    page: 0,
                    filters: {
                        ...prev.filters,
                        searchKey: searchInput
                    }
                };
            });
        }, 500);

        return () => clearTimeout(debounceTimer);
    }, [searchInput]);

    const fetchSectionList = async () => {
        try {
            setLoading(true);
            const listResponse = await SectionServices.sectionList({
                page: paginationModel.page,
                limit: paginationModel.pageSize,
                pageType: 'section',
                excludeReserved: true,
                filters: {
                    searchKey: paginationModel.filters.searchKey,
                    sortBy: paginationModel.filters.sortBy,
                    sortType: paginationModel.filters.sortType,
                    filterOptions: paginationModel.filters.filterOptions || []
                }
            });
            if (listResponse.success == '1') {
                setList(listResponse.data.list);
                setAllRowCount(listResponse.data.totalItems);
            }
        } catch (error) {
            Swal.fire(error.message, '', 'error');
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchSectionList();
    }, [paginationModel]);

    const handleStatusChange = async (data, sectionId) => {
        const result = await Swal.fire({
            text: "Do you want to Change Section Status now?",
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, Change now!',
            cancelButtonText: 'No, Cancel',
            allowOutsideClick: false,
            allowEscapeKey: false,
            showCloseButton: false,
        });

        if (result.isConfirmed) {
            try {
                Swal.fire({
                    title: 'Processing...',
                    text: 'Please wait while we update the section status',
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                    showConfirmButton: false,
                    didOpen: () => {
                        Swal.showLoading();
                    }
                });

                const statusChangeResponse = await SectionServices.toggleStatus(sectionId);

                if (statusChangeResponse.success == '1') {
                    await fetchSectionList();
                    await Swal.fire({
                        icon: 'success',
                        title: 'Success!',
                        text: statusChangeResponse.message || 'Section Status updated successfully',
                        timer: 1500
                    });
                    return true;
                } else {
                    await Swal.fire({
                        icon: 'error',
                        title: 'Error!',
                        text: statusChangeResponse.message || 'Failed to update section status'
                    });
                    return false;
                }
            } catch (error) {
                await Swal.fire({
                    icon: 'error',
                    title: 'Error!',
                    text: 'An error occurred while updating the section status'
                });
                return false;
            }
        } else {
            return false;
        }
    };

    const handleDeleteSection = async (sectionId) => {
        const result = await Swal.fire({
            text: "Are you sure you want to delete this section?",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, Delete!',
            cancelButtonText: 'Cancel',
            allowOutsideClick: false,
            allowEscapeKey: false,
        });

        if (result.isConfirmed) {
            try {
                Swal.fire({
                    title: 'Processing...',
                    text: 'Please wait while we delete the section',
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                    showConfirmButton: false,
                    didOpen: () => {
                        Swal.showLoading();
                    }
                });

                const deleteResponse = await SectionServices.deleteSection(sectionId);

                if (deleteResponse.success == '1') {
                    await fetchSectionList();
                    await Swal.fire({
                        icon: 'success',
                        title: 'Success!',
                        text: deleteResponse.message || 'Section deleted successfully',
                        timer: 1500
                    });
                    return true;
                } else {
                    await Swal.fire({
                        icon: 'error',
                        title: 'Error!',
                        text: deleteResponse.message || 'Failed to delete section'
                    });
                    return false;
                }
            } catch (error) {
                await Swal.fire({
                    icon: 'error',
                    title: 'Error!',
                    text: 'An error occurred while deleting the section'
                });
                return false;
            }
        }
        return false;
    };

    const handlePaginationChange = (newPagination) => {
        setPaginationModel({
            ...paginationModel,
            page: newPagination.page,
            pageSize: newPagination.pageSize
        });
    };

    const [sortModel, setSortModel] = useState([
        {
            field: paginationModel.filters.sortBy,
            sort: paginationModel.filters.sortType?.toLowerCase(),
        },
    ]);

    const handleSortingChange = (newSortModel) => {
        if (newSortModel && newSortModel.length > 0) {
            setSortModel(newSortModel);
            setPaginationModel(prev => ({
                ...prev,
                filters: {
                    ...prev.filters,
                    sortBy: newSortModel[0].field,
                    sortType: newSortModel[0].sort?.toUpperCase()
                }
            }));
        }
    };

    const handleFilterChange = (filterPatch) => {
        setPaginationModel(prev => ({
            ...prev,
            page: 0,
            filters: {
                ...prev.filters,
                ...filterPatch
            }
        }));
    };

    return (
        <SectionContext.Provider value={{
            list,
            setList,
            fetchSectionList,
            loading,
            setLoading,
            allRowCount,
            paginationModel,
            handlePaginationChange,
            handleSortingChange,
            sortModel,
            handleStatusChange,
            handleDeleteSection,
            handleFilterChange,
            searchInput,
            setSearchInput
        }}>
            {loading && <Loading />}
            {!embedded && (
                <PageHeaderLayout headerTitle="Sections">
                    <Button
                        sx={{ textTransform: 'none' }}
                        size="small"
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => navigate('/customer-app/home-screen/section/0?pageType=section')}
                    >
                        Create Section
                    </Button>
                    <Button
                        sx={{ textTransform: 'none' }}
                        size="small"
                        variant="outlined"
                        startIcon={<ListAlt />}
                        onClick={() => navigate('/customer-app/home-screen/section-items?pageType=section')}
                    >
                        Section Items
                    </Button>
                    <Button
                        sx={{ textTransform: 'none' }}
                        size="small"
                        variant="outlined"
                        startIcon={<PhoneAndroid />}
                        onClick={() => setPreviewOpen(true)}
                    >
                        Show Preview
                    </Button>
                </PageHeaderLayout>
            )}
            {embedded ? (
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <SectionList embedded />
                    </Grid>
                </Grid>
            ) : (
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <SectionList />
                    </Grid>
                </Grid>
            )}
            <MobilePreview
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                formData={null}
                sectionId={null}
            />
        </SectionContext.Provider>
    );
};

export default Sections;
